import {sum} from 'BenchlingUtil';
import {TableHeaderRowProps} from 'notebook/tables/core-ui/TableHeaderRow';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import {isRowSelected} from 'notebook/tables/core-ui/TableRowUtils';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {TABLE_COLUMN, Row, RowProperties} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';

export interface TableCellAffixedProps {
  /** Whether the column is frozen. Default is false. */
  isAffixed?: boolean;
  /** Only enabled when a column is frozen and scrolled. Default is false. */
  showAffixedShadow?: boolean;
  /** Must be specified if isAffixed = true. */
  affixedLeftSpacing?: number;
}

type GetTableCellAffixedPropsParameters = {
  /** The column index of the cell */
  j: number;
} & Pick<
  TableRowProps,
  'sliceStart' | 'affixedColumnCount' | 'firstColumnWidth' | 'showAffixedShadow' | 'columnWidths'
>;

/** Make the props needed to determine if a TableCell should be affixed (part of a frozen column) */
export function getTableCellAffixedProps({
  j,
  sliceStart,
  affixedColumnCount,
  firstColumnWidth,
  showAffixedShadow: parentShowAffixedShadow,
  columnWidths,
}: GetTableCellAffixedPropsParameters): TableCellAffixedProps {
  const slicedJ = j - (sliceStart ?? 0);
  const isAffixed = Boolean(affixedColumnCount && slicedJ < affixedColumnCount);
  const affixedLeftSpacing =
    columnWidths && firstColumnWidth ? firstColumnWidth + sum(columnWidths.slice(sliceStart ?? 0, j)) : 0;
  const showAffixedShadow = Boolean(
    parentShowAffixedShadow && affixedColumnCount && slicedJ === affixedColumnCount - 1
  );
  return {
    isAffixed,
    showAffixedShadow,
    affixedLeftSpacing,
  };
}

export type AllowDeleteColumnParameters = {
  columnIndex: number;
} & Pick<TableHeaderRowProps, 'canAddRemoveColumns' | 'getSelectionRange' | 'table'>;

/**
 * Determines if the selected column can be deleted from the table.
 *
 * Uses ColumnConstraint.isColumnUserConfigurable to determine if the column can be deleted. If that field is not
 * present defaults to checking table.legacyFirstEditableColumnIndex.
 *
 * @returns whether the column can be deleted
 */
export function allowDeleteColumn({
  columnIndex,
  canAddRemoveColumns,
  getSelectionRange,
  table,
}: AllowDeleteColumnParameters): boolean {
  if (!canAddRemoveColumns) {
    return false;
  }

  const range = getSelectionRange(TABLE_COLUMN, columnIndex);
  if (range == null) {
    return false;
  }

  // If isColumnUserConfigurable is set on any column constraints, it will be set on all of them, so we
  // will rely on it.
  let isColumnUserConfigurableSet = false;
  for (let i = range.minIndex; i <= range.maxIndex; i++) {
    const columnConstraint = table.columnConstraints?.[i];
    if (columnConstraint && columnConstraint.isColumnUserConfigurable != null) {
      isColumnUserConfigurableSet = true;
      if (!columnConstraint.isColumnUserConfigurable) {
        return false;
      }
    }
  }
  if (isColumnUserConfigurableSet) {
    return true;
  }

  // If isColumnUserConfigurable is not set (e.g. legacy results table), we fall back to the deprecated
  // legacyFirstEditableColumnIndex behavior
  const firstEditableIndex = TableUtil.legacyFirstEditableColumnIndex(table);
  return firstEditableIndex == null || (range != null && range.minIndex >= firstEditableIndex);
}

export function getCellProps(
  props: TableRowProps,
  row: Row,
  evaluatedRow: Array<EvaluatedCell>,
  rowProperties: RowProperties | undefined | null,
  j: number
): {
  isCellLocked: boolean;
  isCellSelected: boolean;
  isCellDblSelected: boolean;
  hasActiveEdit: boolean;
} {
  return {
    isCellLocked: isCellLocked(props, evaluatedRow, rowProperties, j),
    isCellSelected: isCellSelected(props, j),
    isCellDblSelected: j === props.selectedJ,
    hasActiveEdit: Boolean(props.hasActiveCell) && j === props.selectedJ,
  };
}

export function isCellLocked(
  props: TableRowProps,
  evaluatedRow: Array<EvaluatedCell>,
  rowProperties: RowProperties | undefined | null,
  j: number
): boolean {
  return props.locked || Boolean(evaluatedRow[j]?.locked) || rowProperties?.archivePurpose != null;
}

export function isCellSelected(props: TableRowProps, j: number): boolean {
  return isRowSelected(props) && (props.selectedMinJ || 0) <= j && j <= (props.selectedMaxJ || 0);
}

export function cellFillProps(
  props: TableRowProps,
  j: number
): {
  isFillDownBoxLeftEdge: boolean;
  isFillDownBoxRightEdge: boolean;
  isFillDownBoxTopEdge: boolean;
  isFillDownBoxBottomEdge: boolean;
} {
  return {
    isFillDownBoxLeftEdge: props.isFilledDown && (props.fillMinJ === j || j === (props.fillMaxJ || 0) + 1),
    isFillDownBoxRightEdge: props.isFilledDown && (props.fillMaxJ === j || j === (props.fillMinJ || 0) - 1),
    isFillDownBoxTopEdge:
      props.isFillDownBoxTopEdge && (props.fillMinJ || 0) <= j && j <= (props.fillMaxJ || 0),
    isFillDownBoxBottomEdge:
      props.isFillDownBoxBottomEdge && (props.fillMinJ || 0) <= j && j <= (props.fillMaxJ || 0),
  };
}
